// ChatWayo.jsx
// Chat guionizado del Asistente Wayo. No llama a la API: las respuestas están
// escritas, así que no cuesta dinero por visita ni se puede trolear.
// Lo usan dos sitios: la sección <AsistenteDemo /> y la burbuja <AsistenteFab />.
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ChatWayo.css";
import { trackAsistentePregunta } from "../analytics/googleAnalytics";

const GREETING =
  "Buenas tardes, Lucía. Conozco tu objetivo, tus macros y lo que llevas comido hoy. ¿Te repaso el día o te propongo la cena?";

// Todas las respuestas usan los datos del panel de contexto: 1.850 kcal y 130 g
// de proteína de objetivo, 1.420 kcal y 88 g consumidas. Si tocas un número
// aquí, tócalo también en CONTEXTO: la credibilidad de la demo depende de que
// las cuentas cuadren entre sí.
const GUION = [
  {
    id: "cena",
    pregunta: "¿Qué ceno con lo que llevo comido?",
    respuesta:
      "Llevas 1.420 kcal y 88 g de proteína. Te quedan 430 kcal y te faltan 42 g de proteína, así que la cena tiene que tirar de proteína y poco más. Salmón al horno con brócoli y media patata: 410 kcal y 39 g. Te deja el día prácticamente clavado.",
    cta: "Ver recetas",
  },
  {
    id: "proteina",
    pregunta: "¿Me falta proteína hoy?",
    respuesta:
      "Sí, vas justa: 88 g de los 130 g que necesitas. No es drama, es la hora de cenar y aún tienes margen. Con una cena de 40 g lo cierras, y si te cuesta llegar, un yogur griego de postre te suma 10 g más sin apenas calorías.",
    cta: null,
  },
  {
    id: "cerveza",
    pregunta: "¿Puedo tomarme una cerveza?",
    respuesta:
      "Una caña son unas 90 kcal. Con las 430 que te quedan te entra sin romper nada. Lo único: no te aporta proteína y hoy vas corta, así que que el plato de la cena siga siendo proteico y lo compensas.",
    cta: null,
  },
  {
    id: "entreno",
    pregunta: "Dame una rutina para mañana",
    respuesta:
      "Mañana te toca tren superior. 4 series de press banca, 4 de remo con barra, 3 de press militar y 3 de curl, dejando un par de repeticiones en recámara. Diez minutos de movilidad de hombro antes y no te saltes el remo, que es lo que te falta.",
    cta: "Ver entrenos",
  },
];

const CONTEXTO = [
  { etiqueta: "Objetivo", valor: "Perder grasa" },
  { etiqueta: "Meta diaria", valor: "1.850 kcal · 130 g proteína" },
  { etiqueta: "Hoy lleva", valor: "1.420 kcal · 88 g proteína" },
];

const prefiereMenosMovimiento = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// `alto` fija la altura del hilo. Pásalo a null para que el hilo sea flexible y
// se adapte al hueco disponible: es lo que hace la burbuja flotante, que tiene
// que caber entre el header y el borde inferior de la ventana.
export default function ChatWayo({ onCerrar = null, alto = 340, origen = "seccion" }) {
  const [mensajes, setMensajes] = useState([{ autor: "wayo", texto: GREETING, cta: null }]);
  const [pensando, setPensando] = useState(false);
  const [tecleando, setTecleando] = useState("");
  const [usadas, setUsadas] = useState([]);

  const temporizadores = useRef([]);
  const hiloRef = useRef(null);

  const limpiarTemporizadores = useCallback(() => {
    temporizadores.current.forEach(clearTimeout);
    temporizadores.current = [];
  }, []);

  useEffect(() => limpiarTemporizadores, [limpiarTemporizadores]);

  // Autoscroll del hilo, no de la página: con scrollIntoView el navegador
  // arrastraría toda la landing hasta aquí al pulsar un chip.
  useEffect(() => {
    const hilo = hiloRef.current;
    if (hilo) hilo.scrollTop = hilo.scrollHeight;
  }, [mensajes, pensando, tecleando]);

  const preguntar = (item) => {
    if (pensando || tecleando) return;

    trackAsistentePregunta(item.id, origen);

    limpiarTemporizadores();
    setUsadas((previas) => [...previas, item.id]);
    setMensajes((previos) => [...previos, { autor: "usuario", texto: item.pregunta, cta: null }]);
    setPensando(true);

    if (prefiereMenosMovimiento()) {
      setPensando(false);
      setMensajes((previos) => [...previos, { autor: "wayo", texto: item.respuesta, cta: item.cta }]);
      return;
    }

    const arrancarTecleo = setTimeout(() => {
      setPensando(false);
      const letras = item.respuesta.split("");

      letras.forEach((_, indice) => {
        const paso = setTimeout(() => setTecleando(item.respuesta.slice(0, indice + 1)), indice * 14);
        temporizadores.current.push(paso);
      });

      const cierre = setTimeout(() => {
        setTecleando("");
        setMensajes((previos) => [...previos, { autor: "wayo", texto: item.respuesta, cta: item.cta }]);
      }, letras.length * 14 + 120);
      temporizadores.current.push(cierre);
    }, 700);

    temporizadores.current.push(arrancarTecleo);
  };

  const reiniciar = () => {
    limpiarTemporizadores();
    setPensando(false);
    setTecleando("");
    setUsadas([]);
    setMensajes([{ autor: "wayo", texto: GREETING, cta: null }]);
  };

  const pendientes = GUION.filter((item) => !usadas.includes(item.id));
  const ocupado = pensando || Boolean(tecleando);

  return (
    <div className="chatw">
      <div className="chatw-top">
        <img
          className="chatw-avatar"
          src="/assets/img/coach-wayo.webp"
          alt=""
          width="40"
          height="40"
          loading="lazy"
        />
        <div className="chatw-top-text">
          <strong>Wayo</strong>
          <span>Tu entrenador y nutricionista</span>
        </div>

        {/* Los botones van absolutos a la derecha: si fueran parte del flujo,
            aparecer/desaparecer "Reiniciar" descentraría el título. */}
        <div className="chatw-acciones">
          {usadas.length > 0 && (
            <button type="button" className="chatw-reset" onClick={reiniciar}>
              Reiniciar
            </button>
          )}

          {onCerrar && (
            <button
              type="button"
              className="chatw-cerrar"
              onClick={onCerrar}
              aria-label="Cerrar chat"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="chatw-contexto">
        {CONTEXTO.map((dato) => (
          <div className="chatw-dato" key={dato.etiqueta}>
            <span className="chatw-etiqueta">{dato.etiqueta}</span>
            <span className="chatw-valor">{dato.valor}</span>
          </div>
        ))}
      </div>

      <div
        className="chatw-hilo"
        ref={hiloRef}
        style={alto ? { height: alto } : undefined}
        aria-live="polite"
      >
        {mensajes.map((mensaje, indice) => (
          <div key={`${mensaje.autor}-${indice}`} className={`chatw-burbuja chatw-${mensaje.autor}`}>
            <p>{mensaje.texto}</p>
            {mensaje.cta && <span className="chatw-cta">{mensaje.cta}</span>}
          </div>
        ))}

        {pensando && (
          <div className="chatw-burbuja chatw-wayo chatw-pensando">
            <span className="chatw-punto" />
            <span className="chatw-punto" />
            <span className="chatw-punto" />
          </div>
        )}

        {tecleando && (
          <div className="chatw-burbuja chatw-wayo">
            <p>
              {tecleando}
              <span className="chatw-cursor" />
            </p>
          </div>
        )}
      </div>

      <div className="chatw-chips">
        {pendientes.length > 0 ? (
          pendientes.map((item) => (
            <button
              key={item.id}
              type="button"
              className="chatw-chip"
              onClick={() => preguntar(item)}
              disabled={ocupado}
            >
              {item.pregunta}
            </button>
          ))
        ) : (
          <p className="chatw-fin">Esto es con los datos de Lucía. En la app responde con los tuyos.</p>
        )}
      </div>

      <p className="chatw-nota">Conversación de ejemplo con datos de una usuaria ficticia.</p>
    </div>
  );
}
